// src/pages/DeviceTypeManagement/tabs/NetworkTab.jsx
import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  Alert,
  Chip,
  IconButton,
  Stack
} from '@mui/material';
import {
  Lan as NetworkIcon,
  ArrowUpward,
  ArrowDownward,
  Wifi as WifiIcon,
  CellTower as CellIcon,
  Cable as EthIcon
} from '@mui/icons-material';

const INTERFACE_TYPES = [
  { id: 'ethernet', label: 'Ethernet', icon: EthIcon, color: 'success', description: 'Wired LAN connection (most reliable, lowest latency)' },
  { id: 'wifi', label: 'WiFi', icon: WifiIcon, color: 'primary', description: 'Wireless LAN connection (good reliability, moderate latency)' },
  { id: 'cellular', label: 'Cellular', icon: CellIcon, color: 'warning', description: 'Mobile data via SIM card (backup, higher latency, data costs)' },
];

const NetworkTab = ({ deviceType, onChange }) => {
  const priority = deviceType.networkPriority || ['ethernet', 'wifi', 'cellular'];

  const moveUp = (idx) => {
    if (idx === 0) return;
    const np = [...priority];
    [np[idx - 1], np[idx]] = [np[idx], np[idx - 1]];
    onChange({ networkPriority: np });
  };

  const moveDown = (idx) => {
    if (idx >= priority.length - 1) return;
    const np = [...priority];
    [np[idx], np[idx + 1]] = [np[idx + 1], np[idx]];
    onChange({ networkPriority: np });
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <NetworkIcon color="primary" />
        Network Configuration
      </Typography>
      <Divider sx={{ mb: 3 }} />

      {/* Network Priority */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Network Interface Priority
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Define the preferred order of network interfaces for devices of this type.
            The caro-agent will report which interface is active and display the priority on the e-ink screen.
          </Typography>

          <Stack spacing={1}>
            {priority.map((typeId, idx) => {
              const iface = INTERFACE_TYPES.find(t => t.id === typeId) || { id: typeId, label: typeId, color: 'default', description: '', icon: NetworkIcon };
              const Icon = iface.icon;
              return (
                <Card key={typeId} variant="outlined" sx={{ bgcolor: 'background.default' }}>
                  <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 }, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'text.secondary', minWidth: 24, textAlign: 'center' }}>
                      {idx + 1}
                    </Typography>
                    <Chip
                      icon={<Icon />}
                      label={iface.label}
                      color={iface.color}
                      sx={{ fontWeight: 'bold', minWidth: 120 }}
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                      {iface.description}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton
                        size="small"
                        onClick={() => moveUp(idx)}
                        disabled={idx === 0}
                      >
                        <ArrowUpward fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => moveDown(idx)}
                        disabled={idx >= priority.length - 1}
                      >
                        <ArrowDownward fontSize="small" />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>

          <Alert severity="info" sx={{ mt: 2 }}>
            The caro-agent saves this priority to the device config on registration.
            Devices display the current active interface and priority order on their e-ink screen.
          </Alert>
        </CardContent>
      </Card>

      {/* Current Display */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Preview
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This is how the priority will appear on the device display:
          </Typography>
          <Chip
            label={priority.map(t => ({ ethernet: 'eth', wifi: 'wifi', cellular: 'cell' }[t] || t)).join(' > ')}
            variant="outlined"
            sx={{ fontFamily: 'monospace', fontSize: 14, py: 2 }}
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default NetworkTab;
